<template>
  <Modal
    class="scoped"
    :hide-close-button="step === 3"
    :modal-style="{ maxWidth: modalMaxWidth }"
    :persistent="step === 3"
    title="Import Organisations"
  >
    <div style="max-width: 100vw">
      <QCardSection class="row items-center">
        <h4>Import Organisations via CSV</h4>
      </QCardSection>
      <QCardSection v-if="step === 1">
        <div class="row">
          <QInput
            accept=".csv"
            class="full-width"
            filled
            hint="Upload csv"
            square
            type="file"
            @update:model-value="processCsvFile"
          />
        </div>
      </QCardSection>

      <QCardSection
        v-if="step === 1"
        align="right"
      >
        <VueJsonCsv
          class="col"
          :data="sampleCsvData"
          name="sample-organisations.csv"
        >
          <QBtn
            color="primary"
            flat
            label="Sample Organisations.csv"
            no-caps
          />
        </VueJsonCsv>
      </QCardSection>

      <QCardSection v-if="step === 2">
        <div class="row">
          <QForm
            ref="form"
            class="full-width"
          >
            <QMarkupTable
              class="full-width import-table"
              style="height: 600px"
            >
              <thead>
                <tr>
                  <th class="text-left">Name</th>
                  <th
                    v-if="showGlobalCustomerId"
                    class="text-left"
                  >
                    Global Customer ID
                  </th>
                  <th class="text-left">Legal Entity Name</th>
                  <th class="text-left">Acronyms</th>
                  <th class="text-left">Region</th>
                  <th class="text-left">Service Level</th>
                  <th class="text-left">Industry</th>
                  <th class="text-left">Secondary Industry</th>
                  <th class="text-left">Stock Symbol</th>
                  <th class="text-left">Account Manager</th>
                  <th class="text-left">Default Role</th>
                  <th class="text-left">Operational Team</th>
                  <th class="text-left">Intercom Visibility</th>
                  <th class="text-left">Time Zone</th>
                  <th class="text-left">Contract Start</th>
                  <th class="text-left">Contract Expiry</th>
                  <th class="text-left">Default Report User</th>
                  <th class="text-left">PR Agency</th>
                  <th class="text-left">Billable</th>
                  <th class="text-left">Feature Flags</th>
                  <th class="text-left">Content Flags</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(organisation, index) in organisationsToImport"
                  :key="index"
                >
                  <td class="text-left">
                    <QInput
                      v-model="organisation.name"
                      :class="{ 'q-pa-none': organisation.error }"
                      dense
                      filled
                      label="Name"
                      :rules="[
                        (val) => (val && val.length > 0) || 'Must have a Name',
                      ]"
                      square
                    >
                      <template #hint>
                        <small
                          v-if="
                            organisation.name &&
                            organisation.error &&
                            organisation.error.name
                          "
                          class="text-red"
                        >
                          Name has already been taken
                        </small>
                      </template>
                    </QInput>
                  </td>
                  <td
                    v-if="showGlobalCustomerId"
                    class="text-left"
                  >
                    <QInput
                      v-model="organisation.global_customer_id"
                      :class="{ 'q-pa-none': organisation.error }"
                      dense
                      filled
                      label="Global Customer ID"
                      :rules="[
                        (val) => (val && val.length > 0) || 'Must have a GCID',
                      ]"
                      square
                    >
                      <template #hint>
                        <small
                          v-if="
                            organisation.global_customer_id &&
                            organisation.error &&
                            organisation.error.global_customer_id
                          "
                          class="text-red"
                        >
                          CGID has already been taken
                        </small>
                      </template>
                    </QInput>
                  </td>
                  <td class="text-left">
                    <QInput
                      v-model="organisation.legal_entity_name"
                      dense
                      filled
                      label="Name"
                      square
                    />
                  </td>
                  <td class="text-left">
                    <QInput
                      v-model="organisation.acronyms"
                      dense
                      filled
                      label="Acronyms"
                      square
                    />
                  </td>
                  <td class="text-left">
                    <RegionDropdown
                      v-model="organisation.region"
                      label="Select Region"
                      :rules="[
                        (val) =>
                          (val && val.length > 0) || 'Must have a Region',
                      ]"
                    />
                  </td>
                  <td class="text-left">
                    <OrganisationServiceLevelSelector
                      v-model="organisation.service_level"
                      :region="organisation.region"
                    />
                  </td>
                  <td class="text-left">
                    <IndustryDropdown
                      v-model="organisation.industry"
                      label="Select industry"
                      :rules="[
                        (val) =>
                          (val && val.length > 0) || 'Must have an Industry',
                      ]"
                    />
                  </td>
                  <td class="text-left">
                    <IndustryDropdown
                      v-model="organisation.secondary_industry"
                      label="Select secondary industry"
                      :rules="[
                        (val) =>
                          (val && val.length > 0) || 'Must have an Industry',
                      ]"
                      secondary
                    />
                  </td>
                  <td class="text-left">
                    <QInput
                      v-model="organisation.stock_symbol"
                      dense
                      filled
                      label="Stock Symbol"
                      square
                    />
                  </td>
                  <td class="text-left">
                    <AccountManagerDropdown
                      v-model="accountManagerSelection[index]"
                      class="q-pl-sm col-6"
                      dense
                      emit-object
                      label="Account Manager"
                      :rules="[
                        () =>
                          organisation.account_manager_id > 0 ||
                          'Must have an Account Manager',
                      ]"
                      @update:model-value="updateAM($event, index)"
                    />
                  </td>
                  <td class="text-left">
                    <RoleDropdown
                      v-model="organisation.default_role_id"
                      hide-default
                      hide-view-button
                      :rules="[(val) => val > 0 || 'Must have a default Role']"
                    />
                  </td>
                  <td class="text-left">
                    <OperationalTeamSelector
                      v-model="organisation.operational_team_id"
                      :operational-team-options="
                        operationalTeamOptionsForRegion(organisation.region)
                      "
                      :rules="[validateOperationalTeam]"
                    />
                  </td>
                  <td class="text-left">
                    <IntercomVisibilityDropdown
                      v-model="organisation.intercom_visibility"
                      class="q-pb-sm"
                      :rules="[
                        (val) => (val && val.length > 0) || 'Must have a value',
                      ]"
                    />
                  </td>
                  <td class="text-left">
                    <TimezoneDropdown
                      v-model="organisation.time_zone"
                      dense
                      filled
                      :rules="[
                        (val) =>
                          (val && val.length > 0) ||
                          'Must have a valid Timezone',
                      ]"
                    />
                  </td>
                  <td class="text-left">
                    <Calendar
                      v-model="organisation.contract_start"
                      label="Contract Start"
                      square
                    />
                  </td>
                  <td class="text-left">
                    <Calendar
                      v-model="organisation.contract_expiry"
                      label="Contract Expiry"
                      square
                      style="width: 100px"
                    />
                  </td>
                  <td class="text-left">
                    <Checkbox
                      v-model="organisation.default_report_user"
                      class="small"
                    />
                  </td>
                  <td class="text-left">
                    <Checkbox
                      v-model="organisation.pr_agency"
                      class="small"
                    />
                  </td>
                  <td class="text-left">
                    <Checkbox
                      v-model="organisation.default_report_user"
                      class="small"
                    />
                  </td>
                  <td class="text-left">
                    <QInput
                      v-model="organisation.feature_flags"
                      dense
                      filled
                      label="Name"
                      square
                    />
                  </td>
                  <td class="text-left">
                    <QInput
                      v-model="organisation.content_flags"
                      dense
                      filled
                      label="Name"
                      square
                    />
                  </td>
                </tr>
              </tbody>
            </QMarkupTable>
          </QForm>
        </div>
      </QCardSection>

      <QCardActions>
        <div class="full-width flex justify-between">
          <div>
            <QBtn
              v-if="step === 2"
              color="primary"
              flat
              label="Back"
              no-caps
              @click="step = 1"
            />
          </div>
          <div>
            <QBtn
              v-if="step === 1"
              color="black"
              :disabled="importDisabled"
              :label="reviewLabel"
              no-caps
              @click="reviewOrganisations"
            />
            <QBtn
              v-if="step === 2"
              v-close-popup
              color="black"
              :disabled="importDisabled"
              :label="importLabel"
              no-caps
              @click="confirm"
            />
          </div>
        </div>
      </QCardActions>

      <QCardActions v-if="step === 3">
        <div class="row full-width justify-center">
          <span
            >Processing
            <strong>
              {{ importStatuses.length }} / {{ organisationsToImport.length }}
            </strong>
          </span>
          <QLinearProgress
            class="q-mt-sm"
            color="online"
            rounded
            size="10px"
            stripe
            :value="progress"
          />
        </div>
      </QCardActions>
    </div>
  </Modal>
</template>

<script>
import Papa from "papaparse";
import { storeToRefs } from "pinia";
import VueJsonCsv from "vue-json-csv";

import Modal from "shared/components/core/modals/Modal.vue";
import TimezoneDropdown from "shared/components/core/pickers/TimezoneDropdown.vue";
import {
  organisationGovernmentPortfolios,
  organisationIndustries,
  organisationSecondaryIndustries,
  regions,
} from "shared/constants";
import { pluralize } from "shared/helpers/string";
import { OrganisationTeam } from "shared/resources";
import { useGlobalStore } from "shared/stores/global";

import AccountManagerDropdown from "src/components/common/AccountManagerDropdown.vue";
import Calendar from "src/components/common/Calendar.vue";
import IndustryDropdown from "src/components/common/IndustryDropdown.vue";
import IntercomVisibilityDropdown from "src/components/common/IntercomVisibilityDropdown.vue";
import OperationalTeamSelector from "src/components/common/OperationalTeamSelector.vue";
import OrganisationServiceLevelSelector from "src/components/common/OrganisationServiceLevelSelector.vue";
import RegionDropdown from "src/components/common/RegionDropdown.vue";
import RoleDropdown from "src/components/common/RoleDropdown.vue";
import { useGlobalAdminStore } from "src/stores/global";

export default {
  name: "ImportOrganisationsModal",
  components: {
    OperationalTeamSelector,
    AccountManagerDropdown,
    Calendar,
    Modal,
    VueJsonCsv,
    IndustryDropdown,
    IntercomVisibilityDropdown,
    OrganisationServiceLevelSelector,
    RoleDropdown,
    TimezoneDropdown,
    RegionDropdown,
  },
  emits: ["confirm", "cancel"],
  setup() {
    const globalAdminStore = useGlobalAdminStore();
    const { accountManagers } = storeToRefs(globalAdminStore);

    const globalStore = useGlobalStore();
    const { isCisionOne } = storeToRefs(globalStore);

    return {
      accountManagers,
      isCisionOne,
    };
  },
  data() {
    return {
      step: 1,
      organisationsToImport: [],
      accountManagerSelection: {},
      importStatuses: [],
      operationalTeamOptions: [],
    };
  },
  computed: {
    primaryIndustries() {
      return [...organisationIndustries, ...organisationGovernmentPortfolios];
    },
    sampleCsvData() {
      return [
        {
          name: "String - Required",
          ...(this.showGlobalCustomerId
            ? { global_customer_id: "String - Required" }
            : {}),
          legal_entity_name: "String - Optional",
          acronyms: "Semicolon - Separated Strings - Optional",
          region: "Enum - Required",
          service_level: "Enum - Required",
          industry: "String - Required",
          secondary_industry: "String - Required",
          pr_agency: "TRUE/FALSE - Required",
          stock_symbol: "String - Optional",
          account_manager_id: "Number - Required",
          default_role_id: "Number - Required",
          default_outreach_role_id: "Number - Required",
          operational_team_id: "Number - Optional",
          intercom_visibility: "enabled/disabled - Required",
          default_report_user: "TRUE/FALSE - Required",
          time_zone: "String - Required",
          contract_start: "DATE - Optional",
          contract_expiry: "DATE - Optional",
          billable: "TRUE/FALSE - Required",
          feature_flags: "Number (1 - 5) - Optional",
          content_flags: "String - Comma separated codes",
        },
        {
          name: "Sample Organisation",
          ...(this.showGlobalCustomerId ? { global_customer_id: "11111" } : {}),
          legal_entity_name: "Sample Org Ltd.",
          acronyms: '"SO, SampleOrg"',
          ...(this.isCisionOne ? { region: "uk" } : { region: "au" }),
          service_level: "tier_one_plus",
          industry: "Media + Technology",
          secondary_industry: "Media",
          pr_agency: "FALSE",
          stock_symbol: "NAB.AX",
          account_manager_id: 1,
          default_role_id: 1,
          default_outreach_role_id: 18,
          operational_team_id: 1,
          intercom_visibility: "enabled",
          default_report_user: "TRUE",
          time_zone: "Australia/Sydney",
          contract_start: "2023-01-01",
          contract_expiry: "2023-12-31",
          billable: "TRUE",
          feature_flags: 2,
          content_flags:
            "UK-C1-MON-UKPRINT-100CLIPS, UK-C1-MON-AUSNZ-BROADCAST",
        },
      ];
    },
    showGlobalCustomerId() {
      return this.$i18n.locale === "en-GB";
    },
    importDisabled() {
      return !this.organisationsToImport.length;
    },
    pluralString() {
      return `${this.organisationsToImport.length} ${pluralize(
        this.organisationsToImport.length,
        "Organisation"
      )}`;
    },
    reviewLabel() {
      return `Review ${this.pluralString}`;
    },
    importLabel() {
      return `Import ${this.pluralString}`;
    },
    modalMaxWidth() {
      return this.step === 2 ? "100%" : "500px";
    },
    confirmMessage() {
      const failedOrganisations = this.importStatuses.filter(
        (status) => status.error
      );

      const failedRows = failedOrganisations
        .map(({ name, error }) => `<tr><td>${name}</td><td>${error}</td></tr>`)
        .join("\n");

      const successfulOrganisations =
        this.importStatuses.length - failedOrganisations.length;

      let message = `
        <strong>
          Sucessfully imported ${successfulOrganisations} of ${this.pluralString}
        </strong>
      `;

      if (failedOrganisations.length) {
        message += `
          <br/>
          <br/><strong>Failed Organisations:</strong><br/>
          <table class="q-table">
            <thead>
              <tr>
                <td>Name</td>
                <td>Fail Reason</td>
              </tr>
            </thead>
            <tbody>
              ${failedRows}
            </tbody>
          </table>
        `;
      }

      return message;
    },
    progress() {
      if (!this.organisationsToImport?.length) return 0;

      return this.importStatuses.length / this.organisationsToImport.length;
    },
  },
  mounted() {
    this.loadOperationalTeamOptions();
  },
  methods: {
    validateOperationalTeam(value) {
      if (!value) {
        return "Must select a Team";
      }

      return true;
    },
    async loadOperationalTeamOptions() {
      try {
        this.operationalTeamOptions = (
          await OrganisationTeam.includes(["organisation"])
            .where({
              operationalTeam: true,
            })
            .per(999)
            .all()
        ).data;
      } catch (error) {
        this.$error("Unable to load operational teams for organisation", error);
      }
    },
    operationalTeamOptionsForRegion(region) {
      return this.operationalTeamOptions.filter(
        (team) => team.organisation.region === region
      );
    },
    processCsvFile([file]) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const csvFile = event.target.result.trim();
        const parsed = Papa.parse(csvFile).data;
        const columns = parsed.shift();

        this.organisationsToImport = parsed.map((values, organisationIndex) => {
          const organisationAttributes = values.reduce((acc, value, index) => {
            let newValue = value;
            if (["TRUE", "true"].includes(value)) newValue = true;
            if (["FALSE", "false"].includes(value)) newValue = false;
            if (value === "null" || value === "") newValue = null;

            switch (columns[index]) {
              case "contract_start":
              case "contract_expiry":
                acc[columns[index]] = new Date(value);
                break;
              case "global_customer_id":
                acc.global_customer_id = value;
                break;
              case "default_role_id":
              case "default_social_role_id":
              case "account_manager_id":
                acc[columns[index]] = Number(newValue);
                break;
              case "industry":
                acc[columns[index]] = this.primaryIndustries.find(
                  ({ label }) => label === value
                )?.label;

                break;
              case "secondary_industry":
                acc[columns[index]] = organisationSecondaryIndustries.find(
                  ({ label }) => label === value
                )?.value;

                break;
              case "region":
                acc[columns[index]] = regions.find(
                  ({ name, label, value: regionValue }) =>
                    name === newValue ||
                    label === newValue ||
                    regionValue === newValue
                )?.value;

                break;
              default:
                acc[columns[index]] = newValue;
                break;
            }

            return acc;
          }, {});

          const accountManager = this.accountManagers.find(
            ({ id }) => Number(id) === organisationAttributes.account_manager_id
          );

          this.accountManagerSelection[Number(organisationIndex)] =
            accountManager;

          return organisationAttributes;
        });
      };

      reader.readAsText(file);
    },
    async reviewOrganisations() {
      try {
        const response = (
          await this.$streemApiAdmin.post("organisations/validate_import", {
            params: {
              organisations: this.organisationsToImport,
            },
          })
        ).data;

        this.organisationsToImport = response;
        this.step = 2;
      } catch (error) {
        this.$error("Unable to validate organisations", error);
      }
    },
    async confirm() {
      try {
        const valid = await this.$refs.form.validate();

        if (!valid) return false;

        this.step = 3;

        const promises = this.organisationsToImport.map((organisation) =>
          this.saveOrganisation(organisation)
        );

        await Promise.all(promises).then(this.importCompleted);
      } catch (error) {
        this.$error("There was an error importing the organisations", error);
      } finally {
        this.$q.loading.hide();
      }

      return true;
    },
    saveOrganisation(organisation) {
      return this.$streemApiAdmin
        .post("organisations/import", {
          params: {
            organisations: [organisation],
          },
        })
        .then((result) => {
          this.importStatuses.push(result.data[0]);
        })
        .catch((error) => {
          this.importStatuses.push({
            name: organisation.name,
            error: error.message,
          });
        });
    },
    importCompleted() {
      this.$dialog
        .alert({
          title: "Import Complete",
          message: this.confirmMessage,
          persistent: true,
          confirmLabel: "Done",
        })
        .onOk(() => {
          this.cancel();
          this.$emit("confirm");
        });
    },
    cancel() {
      this.$emit("cancel");
    },
    updateAM(event, index) {
      this.organisationsToImport[index].account_manager_id = event.id;

      this.accountManagerSelection = {
        ...this.accountManagerSelection,
        [index.toString()]: event,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
:deep() {
  .import-table {
    .q-field {
      width: 200px;
    }

    .checkbox {
      width: 50px !important;
    }
  }
}
</style>
